import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building, MapPin, Phone, Mail, Globe, Clock, Image as ImageIcon, Video, Save,
  Facebook, Instagram, Linkedin, Twitter, Youtube, Plus, Trash2, Loader2,
  Star, Eye, MessageSquare, Tag, Award, ExternalLink, CheckCircle, AlertCircle,
  Copy, Check, Code, FileText, Bold, Italic, List, Link as LinkIcon, Upload, Share2
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { DragDropUpload } from '../components/ui/drag-drop-upload';
import { api } from '../lib/auth';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXTERNAL_DIRECTORY_URL = process.env.REACT_APP_EXTERNAL_DIRECTORY_URL || 'https://search.aihorsepowered.com';

// TikTok icon (not in lucide)
const TikTokIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const BUSINESS_HOURS_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export default function MyBusinessListingPage() {
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasListing, setHasListing] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const logoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  // Directory Publishing State
  const [publishStatus, setPublishStatus] = useState({ status: 'draft' });
  const [publishing, setPublishing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // File upload handler
  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingVideo;
    setUploading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataUpload
      });
      
      if (response.ok) {
        const data = await response.json();
        const fileUrl = `${API_URL}/api/files/${data.filename}`;
        
        if (type === 'logo') {
          setFormData(prev => ({ ...prev, logo: fileUrl }));
        } else {
          setFormData(prev => ({ ...prev, video_url: fileUrl }));
        }
        toast.success(`${type === 'logo' ? 'Logo' : 'Video'} uploaded successfully!`);
      } else {
        toast.error('Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };
  
  const [formData, setFormData] = useState({
    name: '',
    subcategory: '',
    description: '',
    about_us: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    website: '',
    logo: '',
    video_url: '',
    amenities: [],
    services: [],
    hours: {},
    social_links: {
      facebook: '',
      instagram: '',
      linkedin: '',
      twitter: '',
      tiktok: '',
      youtube: ''
    },
    // AI Business Finder Integration Fields
    is_individual: false,
    ai_agent_name: '',
    ai_agent_personality: '',
    coupon_code: '',
    coupon_description: '',
    offer_stack_details: {
      resort_stay: false,
      travel_card: false,
      custom_offers: []
    },
    ghl_widget_id: '',
    ai_agent_phone: '',
    affiliate_signup_url: '',
    // Tracking Pixels
    tracking_pixels: {
      meta: '',
      google_analytics: '',
      google_tag_manager: '',
      tiktok: '',
      twitter: '',
      linkedin: '',
      postcard: '',
      other: []
    },
    // Social Share / OG Meta
    og_image: '',
    og_title: '',
    og_description: ''
  });
  
  const [newAmenity, setNewAmenity] = useState('');
  
  // Simple formatting helper for the About Us section
  const insertFormatting = (format) => {
    const textarea = document.getElementById('about-us-editor');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.about_us;
    const selected = text.substring(start, end);
    
    let newText = '';
    let newCursorPos = start;
    
    switch (format) {
      case 'bold':
        newText = text.substring(0, start) + `<strong>${selected}</strong>` + text.substring(end);
        newCursorPos = start + 8 + selected.length + 9;
        break;
      case 'italic':
        newText = text.substring(0, start) + `<em>${selected}</em>` + text.substring(end);
        newCursorPos = start + 4 + selected.length + 5;
        break;
      case 'heading':
        newText = text.substring(0, start) + `<h3>${selected}</h3>` + text.substring(end);
        newCursorPos = start + 4 + selected.length + 5;
        break;
      case 'list':
        const listItem = selected ? `<li>${selected}</li>` : '<li></li>';
        newText = text.substring(0, start) + `<ul>\n${listItem}\n</ul>` + text.substring(end);
        newCursorPos = start + 9 + (selected ? selected.length + 9 : 4);
        break;
      default:
        return;
    }
    
    setFormData({...formData, about_us: newText});
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  useEffect(() => {
    loadListing();
    loadPublishStatus();
  }, []);

  const loadPublishStatus = async () => {
    try {
      const res = await api.getBusinessPublishStatus();
      if (res.data?.publish_status) {
        setPublishStatus(res.data.publish_status);
      }
    } catch (error) {
      console.log('No publish status yet');
    }
  };

  const handlePublishToDirectory = async () => {
    setPublishing(true);
    try {
      const res = await api.publishBusinessToDirectory({ publish: true });
      setPublishStatus(res.data.publish_status);
      toast.success('Business published to directory!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setPublishing(true);
    try {
      await api.publishBusinessToDirectory({ publish: false });
      setPublishStatus({ status: 'draft' });
      toast.success('Business unpublished from directory');
    } catch (error) {
      toast.error('Failed to unpublish');
    } finally {
      setPublishing(false);
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await api.getBusinessDirectoryPreview();
      setPreviewData(res.data);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadListing = async () => {
    try {
      const res = await api.get('/api/directory/my-listing');
      if (res.data.has_listing === false) {
        setHasListing(false);
      } else {
        setListing(res.data);
        setFormData({
          name: res.data.name || '',
          subcategory: res.data.subcategory || '',
          description: res.data.description || '',
          about_us: res.data.about_us || '',
          address: res.data.address || '',
          city: res.data.city || '',
          state: res.data.state || '',
          zip_code: res.data.zip_code || '',
          phone: res.data.phone || '',
          email: res.data.email || '',
          website: res.data.website || '',
          logo: res.data.logo || '',
          video_url: res.data.video_url || '',
          amenities: res.data.amenities || [],
          services: res.data.services || [],
          hours: res.data.hours || {},
          social_links: res.data.social_links || {
            facebook: '', instagram: '', linkedin: '', twitter: '', tiktok: '', youtube: ''
          },
          // AI Business Finder Integration Fields
          is_individual: res.data.is_individual || false,
          ai_agent_name: res.data.ai_agent_name || '',
          ai_agent_personality: res.data.ai_agent_personality || '',
          coupon_code: res.data.coupon_code || '',
          coupon_description: res.data.coupon_description || '',
          offer_stack_details: res.data.offer_stack_details || {
            resort_stay: false,
            travel_card: false,
            custom_offers: []
          },
          ghl_widget_id: res.data.ghl_widget_id || '',
          ai_agent_phone: res.data.ai_agent_phone || '',
          affiliate_signup_url: res.data.affiliate_signup_url || '',
          // Tracking Pixels
          tracking_pixels: res.data.tracking_pixels || {
            meta: '', google_analytics: '', google_tag_manager: '',
            tiktok: '', twitter: '', linkedin: '', postcard: '', other: []
          },
          // Social Share / OG Meta
          og_image: res.data.og_image || '',
          og_title: res.data.og_title || '',
          og_description: res.data.og_description || ''
        });
        setHasListing(true);
      }
    } catch (error) {
      console.error('Failed to load listing:', error);
      if (error.response?.status === 404) {
        setHasListing(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/directory/my-listing', formData);
      toast.success('Business listing updated!');
      loadListing();
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const addAmenity = () => {
    if (newAmenity.trim() && !formData.amenities.includes(newAmenity.trim())) {
      setFormData({
        ...formData,
        amenities: [...formData.amenities, newAmenity.trim()]
      });
      setNewAmenity('');
    }
  };

  const removeAmenity = (amenity) => {
    setFormData({
      ...formData,
      amenities: formData.amenities.filter(a => a !== amenity)
    });
  };

  const updateHours = (day, value) => {
    setFormData({
      ...formData,
      hours: { ...formData.hours, [day]: value }
    });
  };

  const updateSocialLink = (platform, value) => {
    setFormData({
      ...formData,
      social_links: { ...formData.social_links, [platform]: value }
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!hasListing) {
    return (
      <DashboardLayout>
        <div className="p-6 pt-8">
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
              <Building className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-3">No Business Listing Yet</h1>
            <p className="text-gray-400 mb-8">
              Complete your onboarding to automatically create your directory listing, 
              or claim an existing business.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link to="/onboarding">
                <Button className="btn-primary">
                  Complete Onboarding
                </Button>
              </Link>
              <a href={EXTERNAL_DIRECTORY_URL} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  Browse Directory
                </Button>
              </a>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 pt-20 max-w-5xl mx-auto" data-testid="my-business-listing-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
              <Building className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">My Business Listing</h1>
              <p className="text-gray-400">Manage your directory presence</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {listing?.id && (
              <a href={`${EXTERNAL_DIRECTORY_URL}/business/${listing.id}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  <ExternalLink className="w-4 h-4 mr-2" /> View Listing
                </Button>
              </a>
            )}
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        {listing && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-[#0d1525] rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="text-sm text-gray-400">Rating</span>
              </div>
              <p className="text-2xl font-bold text-white">{listing.rating?.toFixed(1) || '0.0'}</p>
            </div>
            <div className="bg-[#0d1525] rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-gray-400">Reviews</span>
              </div>
              <p className="text-2xl font-bold text-white">{listing.review_count || 0}</p>
            </div>
            <div className="bg-[#0d1525] rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="w-4 h-4 text-purple-400" />
                <span className="text-sm text-gray-400">Views</span>
              </div>
              <p className="text-2xl font-bold text-white">{listing.views || 0}</p>
            </div>
            <div className="bg-[#0d1525] rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <Award className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-gray-400">Status</span>
              </div>
              <p className={`text-lg font-bold ${listing.is_ai_certified ? 'text-amber-400' : 'text-gray-400'}`}>
                {listing.is_ai_certified ? 'AI Certified' : 'Standard'}
              </p>
            </div>
          </div>
        )}

        {/* Directory Publishing Section */}
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl p-6 border border-amber-500/20 mb-8">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-white text-lg">Publish to AI Business Finder Directory</h2>
                <p className="text-gray-400 text-sm">
                  Make your business visible on the public directory
                </p>
                {publishStatus.status === 'published' && publishStatus.directory_url && (
                  <a 
                    href={publishStatus.directory_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-cyan-400 text-sm hover:underline flex items-center gap-1 mt-1"
                  >
                    View on Directory <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Status Badge */}
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                publishStatus.status === 'published' 
                  ? publishStatus.sync_status === 'synced'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  : publishStatus.status === 'pending'
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
              }`}>
                {publishStatus.status === 'published' ? (
                  publishStatus.sync_status === 'synced' ? (
                    <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Synced</span>
                  ) : (
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Pending Sync</span>
                  )
                ) : publishStatus.status === 'pending' ? (
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>
                ) : (
                  <span className="flex items-center gap-1">Draft</span>
                )}
              </div>
              
              {/* Preview Button */}
              <Button
                variant="outline"
                onClick={loadPreview}
                disabled={previewLoading}
                className="border-white/20 text-white hover:bg-white/10"
              >
                {previewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                Preview
              </Button>
              
              {/* Publish/Unpublish Button */}
              {publishStatus.status === 'published' ? (
                <Button
                  variant="outline"
                  onClick={handleUnpublish}
                  disabled={publishing}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Unpublish
                </Button>
              ) : (
                <Button
                  onClick={handlePublishToDirectory}
                  disabled={publishing}
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white"
                >
                  {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Globe className="w-4 h-4 mr-2" />}
                  Publish to Directory
                </Button>
              )}
            </div>
          </div>
          
          {/* Sync Status Message */}
          {publishStatus.status === 'published' && publishStatus.sync_status === 'pending_sync' && (
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-yellow-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Your business is queued for sync. The directory will update within a few minutes.
              </p>
              {publishStatus.sync_message && (
                <p className="text-yellow-400/70 text-xs mt-1">{publishStatus.sync_message}</p>
              )}
            </div>
          )}
          
          {publishStatus.published_at && (
            <p className="text-gray-500 text-xs mt-3">
              Last published: {new Date(publishStatus.published_at).toLocaleString()}
              {publishStatus.last_sync && ` • Last synced: ${new Date(publishStatus.last_sync).toLocaleString()}`}
            </p>
          )}
        </div>

        {/* Preview Modal */}
        {showPreview && previewData && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0d1525] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-white/10">
              <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#0d1525]">
                <h3 className="text-xl font-bold text-white">Directory Preview</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowPreview(false)}>
                  <span className="text-white text-xl">×</span>
                </Button>
              </div>
              
              <div className="p-6">
                {/* Preview Card */}
                <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-6">
                  <div className="flex items-start gap-4">
                    {previewData.preview_data.logo ? (
                      <img 
                        src={previewData.preview_data.logo} 
                        alt="Business Logo" 
                        className="w-20 h-20 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                        <Building className="w-10 h-10 text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-xl font-bold text-white">{previewData.preview_data.name}</h4>
                        {previewData.preview_data.is_ai_certified && (
                          <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs font-medium">
                            Best of Best
                          </span>
                        )}
                      </div>
                      <p className="text-cyan-400 text-sm">{previewData.preview_data.category}</p>
                      <p className="text-gray-400 text-sm flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {previewData.preview_data.city}, {previewData.preview_data.state}
                      </p>
                      <p className="text-gray-300 text-sm mt-2">{previewData.preview_data.description}</p>
                    </div>
                  </div>
                </div>
                
                {/* Completeness Checklist */}
                <h4 className="text-white font-semibold mb-3">Listing Completeness</h4>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(previewData.completeness).map(([key, value]) => (
                    <div 
                      key={key}
                      className={`flex items-center gap-2 p-3 rounded-lg ${
                        value ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'
                      }`}
                    >
                      {value ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-400" />
                      )}
                      <span className={`text-sm ${value ? 'text-green-400' : 'text-red-400'}`}>
                        {key.replace('has_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
                
                <p className="text-gray-500 text-sm mt-4">
                  Directory URL: <span className="text-cyan-400">{previewData.directory_url}</span>
                </p>
              </div>
              
              <div className="p-6 border-t border-white/10 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowPreview(false)} className="border-white/20 text-white">
                  Close
                </Button>
                {publishStatus.status !== 'published' && (
                  <Button
                    onClick={() => { setShowPreview(false); handlePublishToDirectory(); }}
                    className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white"
                  >
                    <Globe className="w-4 h-4 mr-2" /> Publish Now
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
              <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-cyan-400" />
                Basic Information
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-gray-400">Business Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="mt-1 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Category/Niche</Label>
                  <Input
                    value={formData.subcategory}
                    onChange={(e) => setFormData({...formData, subcategory: e.target.value})}
                    placeholder="e.g., Digital Marketing"
                    className="mt-1 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Website</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    placeholder="https://"
                    className="mt-1 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-gray-400">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={4}
                    placeholder="Tell potential customers about your business..."
                    className="mt-1 bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
            </div>

            {/* About Us - Rich Text Editor */}
            <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
              <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-cyan-400" />
                About Us
              </h2>
              <p className="text-sm text-gray-500 mb-3">
                Create a detailed description of your business. Use the formatting buttons to add structure to your content.
              </p>
              
              {/* Formatting Toolbar */}
              <div className="flex items-center gap-1 p-2 bg-white/5 rounded-t-lg border border-white/10 border-b-0">
                <button
                  type="button"
                  onClick={() => insertFormatting('bold')}
                  className="p-2 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                  title="Bold"
                >
                  <Bold className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('italic')}
                  className="p-2 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                  title="Italic"
                >
                  <Italic className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('heading')}
                  className="p-2 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors text-sm font-bold"
                  title="Heading"
                >
                  H
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('list')}
                  className="p-2 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                  title="Bullet List"
                >
                  <List className="w-4 h-4" />
                </button>
                <span className="text-gray-600 text-xs ml-2">Select text, then click to format</span>
              </div>
              
              {/* Editor */}
              <Textarea
                id="about-us-editor"
                value={formData.about_us}
                onChange={(e) => setFormData({...formData, about_us: e.target.value})}
                rows={10}
                placeholder="Share your story, services, history, mission, and what makes your business unique...

Example:
<h3>Our Story</h3>
Founded in 2020, we have been dedicated to providing exceptional service...

<h3>Our Services</h3>
<ul>
<li>Service 1</li>
<li>Service 2</li>
</ul>"
                className="mt-0 bg-white/5 border-white/10 text-white rounded-t-none font-mono text-sm"
              />
              
              {/* Preview */}
              {formData.about_us && (
                <div className="mt-4">
                  <Label className="text-gray-400 mb-2 block">Preview:</Label>
                  <div 
                    className="p-4 bg-white/5 rounded-lg border border-white/10 prose prose-invert prose-cyan max-w-none text-gray-300"
                    dangerouslySetInnerHTML={{ __html: formData.about_us }}
                  />
                </div>
              )}
            </div>

            {/* Contact & Location */}
            <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
              <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-cyan-400" />
                Contact & Location
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    className="mt-1 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Email</Label>
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    type="email"
                    className="mt-1 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-gray-400">Street Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="mt-1 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="mt-1 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-400">State</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      className="mt-1 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">ZIP</Label>
                    <Input
                      value={formData.zip_code}
                      onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                      className="mt-1 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Media */}
            <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
              <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-cyan-400" />
                Media
              </h2>
              <div className="space-y-6">
                {/* Logo Upload - Drag & Drop */}
                <div>
                  <Label className="text-gray-400 mb-2 block">Business Logo</Label>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <DragDropUpload
                      type="image"
                      currentFile={formData.logo}
                      onUpload={(url) => setFormData({...formData, logo: url})}
                      onRemove={() => setFormData({...formData, logo: ''})}
                    />
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500">Or paste a URL:</p>
                      <Input
                        value={formData.logo}
                        onChange={(e) => setFormData({...formData, logo: e.target.value})}
                        placeholder="https://example.com/logo.png"
                        className="bg-white/5 border-white/10 text-white text-sm"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Video Upload - Drag & Drop */}
                <div>
                  <Label className="text-gray-400 mb-2 block">Business Video</Label>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <DragDropUpload
                      type="video"
                      currentFile={formData.video_url}
                      onUpload={(url) => setFormData({...formData, video_url: url})}
                      onRemove={() => setFormData({...formData, video_url: ''})}
                    />
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500">Or paste a YouTube/Vimeo URL:</p>
                      <Input
                        value={formData.video_url}
                        onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                        placeholder="https://youtube.com/watch?v=..."
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
              <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-cyan-400" />
                Amenities & Features
              </h2>
              <div className="flex flex-wrap gap-2 mb-4">
                {formData.amenities.map((amenity, i) => (
                  <span key={i} className="flex items-center gap-1 px-3 py-1 bg-cyan-500/10 text-cyan-400 text-sm rounded-full border border-cyan-500/20">
                    {amenity}
                    <button onClick={() => removeAmenity(amenity)} className="ml-1 hover:text-red-400">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newAmenity}
                  onChange={(e) => setNewAmenity(e.target.value)}
                  placeholder="Add amenity (e.g., Free WiFi, Parking)"
                  className="bg-white/5 border-white/10 text-white"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAmenity())}
                />
                <Button onClick={addAmenity} variant="outline" className="border-white/20 text-white">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Social Links */}
            <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
              <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-cyan-400" />
                Social Media
              </h2>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Facebook className="w-5 h-5 text-blue-500" />
                  <Input
                    value={formData.social_links.facebook}
                    onChange={(e) => updateSocialLink('facebook', e.target.value)}
                    placeholder="Facebook URL"
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Instagram className="w-5 h-5 text-pink-500" />
                  <Input
                    value={formData.social_links.instagram}
                    onChange={(e) => updateSocialLink('instagram', e.target.value)}
                    placeholder="Instagram URL"
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Linkedin className="w-5 h-5 text-blue-400" />
                  <Input
                    value={formData.social_links.linkedin}
                    onChange={(e) => updateSocialLink('linkedin', e.target.value)}
                    placeholder="LinkedIn URL"
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Twitter className="w-5 h-5 text-sky-400" />
                  <Input
                    value={formData.social_links.twitter}
                    onChange={(e) => updateSocialLink('twitter', e.target.value)}
                    placeholder="Twitter/X URL"
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <TikTokIcon />
                  <Input
                    value={formData.social_links.tiktok}
                    onChange={(e) => updateSocialLink('tiktok', e.target.value)}
                    placeholder="TikTok URL"
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Youtube className="w-5 h-5 text-red-500" />
                  <Input
                    value={formData.social_links.youtube}
                    onChange={(e) => updateSocialLink('youtube', e.target.value)}
                    placeholder="YouTube URL"
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
              <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-400" />
                Business Hours
              </h2>
              <div className="space-y-2">
                {BUSINESS_HOURS_DAYS.map(day => (
                  <div key={day} className="flex items-center gap-2">
                    <span className="w-16 text-xs text-gray-400 capitalize">{day.slice(0, 3)}</span>
                    <Input
                      value={formData.hours[day] || ''}
                      onChange={(e) => updateHours(day, e.target.value)}
                      placeholder="9am - 5pm or Closed"
                      className="bg-white/5 border-white/10 text-white text-sm h-8"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Directory Integration Settings */}
            <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/10 rounded-xl p-6 border border-amber-500/20">
              <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-amber-400" />
                AI Business Finder Settings
              </h2>
              <div className="space-y-4">
                {/* Business Type */}
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white text-sm font-medium">Individual Professional</p>
                    <p className="text-gray-500 text-xs">Check if you&apos;re an agent, rep, or solo pro (not a business)</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={formData.is_individual || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_individual: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                  </label>
                </div>

                {/* AI Hero Customization */}
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Custom AI Agent Name</label>
                  <Input
                    value={formData.ai_agent_name || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, ai_agent_name: e.target.value }))}
                    placeholder="e.g., MORGAN (leave blank for default)"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">AI Agent Personality</label>
                  <Input
                    value={formData.ai_agent_personality || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, ai_agent_personality: e.target.value }))}
                    placeholder="e.g., Friendly expert in home services"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>

                {/* Coupon & Offers */}
                <div className="pt-2 border-t border-white/10">
                  <p className="text-amber-400 text-sm font-medium mb-2">Coupon & Offers</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Coupon Code</label>
                      <Input
                        value={formData.coupon_code || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, coupon_code: e.target.value }))}
                        placeholder="e.g., SAVE20"
                        className="bg-white/5 border-white/10 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Coupon Description</label>
                      <Input
                        value={formData.coupon_description || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, coupon_description: e.target.value }))}
                        placeholder="e.g., 20% off first order"
                        className="bg-white/5 border-white/10 text-white text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Offer Stack */}
                <div className="flex flex-wrap gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input 
                      type="checkbox"
                      checked={formData.offer_stack_details?.resort_stay || false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        offer_stack_details: { ...prev.offer_stack_details, resort_stay: e.target.checked }
                      }))}
                      className="rounded border-gray-500 bg-white/5 text-amber-500"
                    />
                    Include Resort Stay Offer
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input 
                      type="checkbox"
                      checked={formData.offer_stack_details?.travel_card || false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        offer_stack_details: { ...prev.offer_stack_details, travel_card: e.target.checked }
                      }))}
                      className="rounded border-gray-500 bg-white/5 text-amber-500"
                    />
                    Include Travel Card Offer
                  </label>
                </div>

                {/* Custom AI Agent Phone Number */}
                <div className="pt-2 border-t border-white/10">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Custom AI Agent Phone Number (optional)</label>
                    <Input
                      value={formData.ai_agent_phone || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, ai_agent_phone: e.target.value }))}
                      placeholder="e.g., +1 (555) 123-4567"
                      className="bg-white/5 border-white/10 text-white text-sm"
                    />
                  </div>
                </div>

                {/* Affiliate URL */}
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Affiliate Signup URL</label>
                  <Input
                    value={formData.affiliate_signup_url || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, affiliate_signup_url: e.target.value }))}
                    placeholder="https://your-affiliate-program.com/signup"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Tracking Pixels Section */}
            <div className="bg-gradient-to-br from-purple-900/30 to-gray-800/50 rounded-xl p-6 border border-purple-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-white">Tracking Pixels</h3>
                  <p className="text-xs text-gray-400">Track visitors on your directory listing</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {/* Meta Pixel */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 008.44-9.9c0-5.53-4.5-10.02-10-10.02z"/></svg>
                    Meta (Facebook) Pixel ID
                  </label>
                  <Input
                    value={formData.tracking_pixels?.meta || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tracking_pixels: { ...prev.tracking_pixels, meta: e.target.value }
                    }))}
                    placeholder="e.g., 1234567890123456"
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                </div>

                {/* Google Analytics */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block flex items-center gap-2">
                    <svg className="w-4 h-4 text-yellow-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/></svg>
                    Google Analytics ID
                  </label>
                  <Input
                    value={formData.tracking_pixels?.google_analytics || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tracking_pixels: { ...prev.tracking_pixels, google_analytics: e.target.value }
                    }))}
                    placeholder="e.g., G-XXXXXXXXXX or UA-XXXXXXXX-X"
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                </div>

                {/* Google Tag Manager */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/></svg>
                    Google Tag Manager ID
                  </label>
                  <Input
                    value={formData.tracking_pixels?.google_tag_manager || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tracking_pixels: { ...prev.tracking_pixels, google_tag_manager: e.target.value }
                    }))}
                    placeholder="e.g., GTM-XXXXXXX"
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                </div>

                {/* TikTok Pixel */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block flex items-center gap-2">
                    <TikTokIcon className="w-4 h-4 text-pink-400" />
                    TikTok Pixel ID
                  </label>
                  <Input
                    value={formData.tracking_pixels?.tiktok || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tracking_pixels: { ...prev.tracking_pixels, tiktok: e.target.value }
                    }))}
                    placeholder="e.g., XXXXXXXXXXXXXXXXXX"
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                </div>

                {/* Twitter Pixel */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block flex items-center gap-2">
                    <svg className="w-4 h-4 text-sky-400" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    Twitter (X) Pixel ID
                  </label>
                  <Input
                    value={formData.tracking_pixels?.twitter || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tracking_pixels: { ...prev.tracking_pixels, twitter: e.target.value }
                    }))}
                    placeholder="e.g., o1234"
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                </div>

                {/* LinkedIn Insight Tag */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                    LinkedIn Insight Tag
                  </label>
                  <Input
                    value={formData.tracking_pixels?.linkedin || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tracking_pixels: { ...prev.tracking_pixels, linkedin: e.target.value }
                    }))}
                    placeholder="e.g., 1234567"
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                </div>

                {/* Postcard Pixel */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block flex items-center gap-2">
                    <Mail className="w-4 h-4 text-green-400" />
                    Postcard/Direct Mail Pixel
                  </label>
                  <Input
                    value={formData.tracking_pixels?.postcard || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tracking_pixels: { ...prev.tracking_pixels, postcard: e.target.value }
                    }))}
                    placeholder="e.g., Your postcard tracking ID"
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                </div>

                {/* Other Pixels */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block flex items-center gap-2">
                    <Code className="w-4 h-4 text-gray-400" />
                    Other Tracking Code (full script)
                  </label>
                  <textarea
                    value={(formData.tracking_pixels?.other || []).join('\n')}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tracking_pixels: { 
                        ...prev.tracking_pixels, 
                        other: e.target.value ? e.target.value.split('\n').filter(line => line.trim()) : []
                      }
                    }))}
                    placeholder="Paste additional tracking scripts here (one per line)"
                    rows={3}
                    className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg p-2 font-mono"
                  />
                </div>
              </div>
            </div>

            {/* Social Share / OG Meta Section */}
            <div className="bg-gradient-to-br from-pink-900/30 to-gray-800/50 rounded-xl p-6 border border-pink-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-pink-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Social Share Preview</h3>
                  <p className="text-xs text-gray-400">Customize how your listing appears when shared</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* OG Image */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Share Image URL</label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.og_image || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, og_image: e.target.value }))}
                      placeholder="https://your-image-url.com/image.jpg"
                      className="bg-white/5 border-white/10 text-white text-sm flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, og_image: formData.logo }))}
                      disabled={!formData.logo}
                      className="text-xs"
                    >
                      Use Logo
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Recommended: 1200x630 pixels for best results</p>
                </div>

                {/* OG Title */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Share Headline</label>
                  <Input
                    value={formData.og_title || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, og_title: e.target.value }))}
                    placeholder={formData.name || "Your business name"}
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank to use business name</p>
                </div>

                {/* OG Description */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Share Description</label>
                  <textarea
                    value={formData.og_description || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, og_description: e.target.value }))}
                    placeholder={(formData.description || "").substring(0, 160) || "A brief description of your business..."}
                    rows={2}
                    maxLength={160}
                    className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg p-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">{(formData.og_description || '').length}/160 characters</p>
                </div>

                {/* Preview Card */}
                <div className="mt-4 p-3 bg-white/5 rounded-lg border border-white/10">
                  <p className="text-xs text-gray-400 mb-2">Preview (Facebook/LinkedIn/Twitter)</p>
                  <div className="bg-white rounded-lg overflow-hidden">
                    {/* Preview Image */}
                    <div className="w-full h-32 bg-gray-200 relative">
                      {(formData.og_image || formData.logo) ? (
                        <img 
                          src={formData.og_image || formData.logo} 
                          alt="Share preview"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <ImageIcon className="w-8 h-8" />
                        </div>
                      )}
                    </div>
                    {/* Preview Text */}
                    <div className="p-3">
                      <p className="text-xs text-gray-500 uppercase">search.aihorsepowered.com</p>
                      <p className="font-semibold text-gray-900 text-sm line-clamp-1">
                        {formData.og_title || formData.name || 'Your Business Name'}
                      </p>
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {formData.og_description || (formData.description || '').substring(0, 160) || 'Your business description will appear here...'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Published Content */}
            {listing?.published_content?.length > 0 && (
              <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
                <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-cyan-400" />
                  Published Content
                </h2>
                <div className="space-y-2">
                  {listing.published_content.slice(0, 5).map(content => (
                    <div key={content.id} className="p-2 bg-white/5 rounded-lg">
                      <p className="text-sm text-white line-clamp-1">{content.title}</p>
                      <span className="text-xs text-gray-500 capitalize">{content.content_type.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
                <Link to="/publishing">
                  <Button variant="outline" size="sm" className="w-full mt-3 border-white/20 text-white hover:bg-white/10">
                    <Plus className="w-4 h-4 mr-1" /> Publish More
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
